/**
 * The shapes the Signals mark moves through while an answer is worked out.
 *
 * Every shape is the same six nodes in a new place, so the mark never stops
 * being the logo's graph: it turns into a ring, a triangle, a rising line and a
 * hub, and back into the logo. One shape per 2.6s beat, the beat the status
 * word already changes on, so the mark and the word move as one object.
 *
 * Node i in one shape becomes node i in the next. The orders below are picked
 * so each node travels a short way rather than crossing the whole mark.
 */

export type Point = [number, number];

export interface MarkShape {
  name: string;
  points: Point[];
  edges: [number, number][];
}

export const MARK_SHAPES: MarkShape[] = [
  {
    // The logo itself: top, upper left, upper right, centre, lower left,
    // lower right — the same coordinates as the static mark.
    name: 'graph',
    points: [
      [50, 12],
      [14, 40],
      [86, 40],
      [50, 52],
      [26, 88],
      [76, 88]
    ],
    edges: [
      [0, 1],
      [0, 2],
      [0, 3],
      [1, 3],
      [2, 3],
      [4, 3],
      [5, 3],
      [4, 5]
    ]
  },
  {
    name: 'ring',
    points: [
      [50, 12],
      [17, 31],
      [83, 31],
      [83, 69],
      [17, 69],
      [50, 88]
    ],
    edges: [
      [0, 1],
      [0, 2],
      [2, 3],
      [3, 5],
      [5, 4],
      [4, 1]
    ]
  },
  {
    name: 'triangle',
    points: [
      [50, 10],
      [30, 48],
      [70, 48],
      [50, 86],
      [10, 86],
      [90, 86]
    ],
    edges: [
      [0, 1],
      [1, 4],
      [4, 3],
      [3, 5],
      [5, 2],
      [2, 0],
      [1, 2],
      [1, 3],
      [2, 3]
    ]
  },
  {
    name: 'chart',
    points: [
      [76, 44],
      [12, 80],
      [90, 16],
      [44, 64],
      [28, 58],
      [60, 40]
    ],
    edges: [
      [1, 4],
      [4, 3],
      [3, 5],
      [5, 0],
      [0, 2]
    ]
  },
  {
    name: 'hub',
    points: [
      [50, 13],
      [16, 39],
      [84, 39],
      [50, 50],
      [29, 80],
      [71, 80]
    ],
    edges: [
      [3, 0],
      [3, 1],
      [3, 2],
      [3, 4],
      [3, 5]
    ]
  }
];

export const MARK_NODE_COUNT = 6;

/** Every pair of nodes: each shape lights a subset of these lines. */
export const MARK_PAIRS: [number, number][] = (() => {
  const pairs: [number, number][] = [];
  for (let a = 0; a < MARK_NODE_COUNT; a++) {
    for (let b = a + 1; b < MARK_NODE_COUNT; b++) pairs.push([a, b]);
  }
  return pairs;
})();

/** Which of MARK_PAIRS a shape draws. */
export function litPairs(shape: MarkShape): boolean[] {
  const lit = new Set(
    shape.edges.map(([a, b]) => `${Math.min(a, b)}-${Math.max(a, b)}`)
  );
  return MARK_PAIRS.map(([a, b]) => lit.has(`${a}-${b}`));
}

/** Ease in and out, so a node leaves gently and lands gently. */
export function easeInOut(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
}

/** The nodes `t` of the way from `from` to `to` (t is clamped to 0..1). */
export function morphPoints(from: Point[], to: Point[], t: number): Point[] {
  const e = easeInOut(t);
  return from.map(([x, y], i) => [
    x + (to[i][0] - x) * e,
    y + (to[i][1] - y) * e
  ]);
}
